import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  FileCode, 
  Copy, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Check, 
  AlertCircle, 
  Sparkles, 
  Cpu, 
  Zap,
  Layers,
  Settings
} from 'lucide-react';

const SAMPLE_CODES = {
  html: `<!-- Master Website Landing Template -->\n<div class="container" id="landing-hero">\n  <header class="header-main">\n    <h1>Welcome to Toolmetrico Workspace</h1>\n    <p class="subtitle">Secure browser-native utility workspace designed for modern devs</p>\n  </header>\n\n  <main class="content">\n    <section class="badge-row">\n      <span class="badge">100% Client-Side</span>\n      <span class="badge">GDPR Compliant</span>\n    </section>\n  </main>\n</div>`,
  css: `/* Core Theme Variables */\n:root {\n  --primary: #0575fb;\n  --secondary: #0ea5e9;\n  --border-color: rgba(226, 232, 240, 0.8);\n}\n\n.container {\n  max-width: 1200px;\n  margin: 0 auto;\n  padding: 24px;\n}\n\n.badge {\n  background: rgba(5, 117, 251, 0.05);\n  color: var(--primary);\n  padding: 4px 10px;\n  border-radius: 99px;\n}`,
  js: `// Dynamic Greeting Script\nfunction greetUser(username) {\n  const now = new Date();\n  const hour = now.getHours();\n  \n  let greeting = "Good morning";\n  if (hour >= 12 && hour < 18) {\n    greeting = "Good afternoon";\n  } else if (hour >= 18) {\n    greeting = "Good evening";\n  }\n  \n  console.log(greeting + ", " + username + "! Welcome back.");\n  return greeting;\n}`
};

export const CodeMinifier = () => {
  const { withAd, toast } = useAdRedirect();
  const [activeTab, setActiveTab] = useState('html'); // html, css, js
  const [inputCode, setInputCode] = useState('');
  const [minifiedCode, setMinifiedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Compression analytics
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);

  useEffect(() => {
    // Pre-populate with sample codes
    setInputCode(SAMPLE_CODES[activeTab]);
    setMinifiedCode('');
    setOriginalSize(0);
    setCompressedSize(0);
  }, [activeTab]);

  const handleMinify = () => {
    if (!inputCode.trim()) return;
    setIsProcessing(true);

    setTimeout(() => {
      let minified = '';
      const orig = inputCode;

      if (activeTab === 'html') {
        minified = minifyHTML(orig);
      } else if (activeTab === 'css') {
        minified = minifyCSS(orig);
      } else if (activeTab === 'js') {
        minified = minifyJS(orig);
      }

      setMinifiedCode(minified);
      setOriginalSize(new Blob([orig]).size);
      setCompressedSize(new Blob([minified]).size);
      setIsProcessing(false);
    }, 600);
  };

  // HTML MINIFIER ENGINE
  const minifyHTML = (html) => {
    return html
      .replace(/<!--[\s\S]*?-->/g, '') // Remove comments
      .replace(/>\s+</g, '><')        // Strip white spaces between tags
      .replace(/\s{2,}/g, ' ')         // Collapse sequential spaces
      .trim();
  };

  // CSS MINIFIER ENGINE
  const minifyCSS = (css) => {
    return css
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove comments
      .replace(/\s+/g, ' ')             // Collapse whitespace
      .replace(/\s*([{\}:;])\s*/g, '$1') // Strip spaces around syntax tokens
      .replace(/;}/g, '}')               // Delete trailing semicolons in blocks
      .trim();
  };

  // JS MINIFIER ENGINE (Advanced regex parsing)
  const minifyJS = (js) => {
    return js
      .replace(/\/\*[\s\S]*?\*\//g, '') // Remove multiline comments
      .replace(/(?:^|[^:])\/\/.*$/gm, '') // Remove single-line comments
      .replace(/\s+/g, ' ')               // Collapse whitespace
      .replace(/\s*([=\+\-\*\/\{\}\(\);,\[\]])\s*/g, '$1') // Strip spaces around syntax rules
      .trim();
  };

  const handleCopy = () => {
    if (!minifiedCode) return;
    navigator.clipboard.writeText(minifiedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!minifiedCode) return;
    const blob = new Blob([minifiedCode], { type: 'text/plain' });
    const localUrl = URL.createObjectURL(blob);
    
    const ext = activeTab === 'html' ? 'html' : activeTab === 'css' ? 'css' : 'js';
    const link = document.createElement('a');
    link.href = localUrl;
    link.download = `toolmetrico-${Date.now()}.min.${ext}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(localUrl);
  };

  const formatKB = (bytes) => {
    if (!bytes) return '0 B';
    return `${(bytes / 1024).toFixed(3)} KB`;
  };

  const calculateSaved = () => {
    if (!originalSize || !compressedSize) return 0;
    const diff = originalSize - compressedSize;
    return Math.max(0, Math.round((diff / originalSize) * 100));
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%',
      paddingBottom: '60px'
    }}>
      <SEO 
        title="Free Code Minifier" 
        description="Minify HTML, CSS, and JS online instantly. Reduce file sizes, improve website loading speeds, and optimize performance. Free, offline, client-side code minification."
        keywords="code minifier, html minifier, css minifier, js minifier, javascript compressor, optimize code, secure code minifier"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '24px',
        fontSize: '0.9rem'
      }}>
        <Link to="/" style={{
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'var(--transition-smooth)'
        }} className="breadcrumb-back">
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Code Compressor & Minifier</span>
      </div>



      {/* Code language segment tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--border-color)',
        marginBottom: '32px',
        gap: '24px'
      }}>
        {[
          { id: 'html', label: 'HTML Minifier' },
          { id: 'css', label: 'CSS Minifier' },
          { id: 'js', label: 'JS Minifier' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid var(--primary)' : '3px solid transparent',
              color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              fontSize: '1.05rem',
              fontWeight: 800,
              padding: '12px 6px',
              cursor: 'pointer',
              transition: 'var(--transition-smooth)'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Workspace Core Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '32px',
        alignItems: 'start'
      }} className="minifier-workspace-grid">
        
        {/* Left Input Column Area */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={labelStyle}>Original {activeTab.toUpperCase()} input code</span>
              <button
                onClick={() => setInputCode('')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  padding: 0
                }}
              >
                Clear Input
              </button>
            </div>

            <textarea
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              placeholder={`Paste your raw, uncompressed ${activeTab.toUpperCase()} code structure here...`}
              style={{
                width: '100%',
                height: '320px',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                padding: '16px',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box'
              }}
            />

            <button
              onClick={handleMinify}
              disabled={isProcessing || !inputCode.trim()}
              style={{
                height: '46px',
                border: 'none',
                background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%)',
                color: '#ffffff',
                borderRadius: '10px',
                fontSize: '0.9rem',
                fontWeight: 700,
                cursor: isProcessing || !inputCode.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'var(--transition-smooth)'
              }}
              className="minify-action-btn"
            >
              {isProcessing ? <RefreshCw size={16} className="spinner" /> : <Settings size={16} />}
              {isProcessing ? 'Compressing Syntax Trees...' : `Compress & Minify ${activeTab.toUpperCase()}`}
            </button>
          </div>
        </div>

        {/* Right Output Column Area */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Output text area card */}
          <div style={cardStyle}>
            <span style={labelStyle}>Minified Code Output</span>

            <textarea
              readOnly
              value={minifiedCode}
              placeholder="Minified results will compile here..."
              style={{
                width: '100%',
                height: '180px',
                fontFamily: 'monospace',
                fontSize: '0.825rem',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                background: '#0a0f1d',
                color: '#a5f3fc',
                padding: '16px',
                outline: 'none',
                resize: 'none',
                boxSizing: 'border-box'
              }}
            />

            {/* Quick Actions drawer */}
            {minifiedCode && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  onClick={handleCopy}
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: '#ffffff',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Copy size={14} />
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                <AdToast message={toast} />
                <button
                  onClick={withAd('code-minify', handleDownload)}
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#059669',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '0.825rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Download size={14} /> Download File
                </button>
              </div>
            )}
          </div>

          {/* Analytics card metrics */}
          {minifiedCode && (
            <div style={{
              background: '#ffffff',
              border: '1px solid var(--border-color)',
              borderRadius: '20px',
              padding: '24px',
              boxShadow: 'var(--shadow-md)'
            }}>
              <span style={{ ...labelStyle, color: '#059669', marginBottom: '12px', display: 'block' }}>Compression Statistics</span>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>BEFORE</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: 'var(--text-primary)' }}>{formatKB(originalSize)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>AFTER</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#059669' }}>{formatKB(compressedSize)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 800 }}>SAVED</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0ea5e9' }}>{calculateSaved()}%</div>
                </div>
              </div>

              <div style={{
                background: 'rgba(5, 150, 105, 0.05)',
                color: '#059669',
                border: '1px solid rgba(5, 150, 105, 0.15)',
                padding: '10px 14px',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: 700,
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Sparkles size={14} />
                Successfully collapsed structures client-side! Saved {formatKB(originalSize - compressedSize)} of storage footprint.
              </div>
            </div>
          )}

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .minifier-workspace-grid {
            grid-template-columns: 1fr 420px !important;
          }
        }

        .minify-action-btn:hover {
          opacity: 0.95;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(5, 117, 251, 0.15) !important;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

    {/* SEO: FAQ & Related Tools */}
      <AdUnit />
      <ToolPageExtras toolId="code-minifier" />
    </div>
  );
};

// Layout rules definitions
const cardStyle = {
  background: '#ffffff',
  border: '1px solid var(--border-color)',
  borderRadius: '20px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  boxShadow: 'var(--shadow-sm)'
};

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: 800,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

export default CodeMinifier;
